import {uniq} from 'lodash/fp';
import {useMemo} from 'react';
import {PickDeep} from 'type-fest';

import {PolicyFilter} from 'modules/decisionsNext/api/types';

import {usePolicyAgencyCodesTreeQuery} from 'modules/decisionsNext/api/trees/policyAgencyCodesTree.generated';
import {useIsDownMd} from 'utils/hooks/useMediaQuery';

import {CheckboxGroupBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/CheckboxGroupBlock';

export const NoPolicySourceCodeOption = {label: 'No Policy Source Code', value: 'No Policy Source Code'};

type PolicySourcesBlockProps = {
  policySourcesFilterStore: PickDeep<PolicyFilter, 'policySourcesFilter'>;
  setPolicySourcesFilterStore: (policyFilter: Pick<PolicyFilter, 'policySourcesFilter'>) => void;
  isIgnored?: boolean;
};

export const PolicySourcesBlock = ({
  policySourcesFilterStore,
  setPolicySourcesFilterStore,
  isIgnored,
}: PolicySourcesBlockProps) => {
  const isDownMd = useIsDownMd();
  const {data, loading} = usePolicyAgencyCodesTreeQuery();
  const options = useMemo(
    () => data?.sourceCodes.policySources.map(source => ({label: source.name, value: source.id})) ?? [],
    [data]
  );

  const {policySourcesFilter} = policySourcesFilterStore;
  const policySources = useMemo(() => {
    if (policySourcesFilter?.isEmpty) {
      return uniq([...(policySourcesFilter?.sources || []), NoPolicySourceCodeOption.value]);
    }

    return uniq([...(policySourcesFilter?.sources || [])]);
  }, [policySourcesFilter?.isEmpty, policySourcesFilter?.sources]);

  if (loading) {
    return null;
  }

  return (
    <CheckboxGroupBlock
      row={!isDownMd}
      options={[...options, NoPolicySourceCodeOption]}
      value={policySources}
      dataTestId='policy-source-block'
      label='Policy Source Code'
      disabled={isIgnored}
      onChange={policySources => {
        const policySourcesFilter = policySources.includes(NoPolicySourceCodeOption.value)
          ? {isEmpty: true, sources: policySources.filter(source => source !== NoPolicySourceCodeOption.value)}
          : {isEmpty: undefined, sources: policySources};

        setPolicySourcesFilterStore({policySourcesFilter});
      }}
    />
  );
};
